import clsx from 'clsx'
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { Controller, ControllerRenderProps, FieldValues } from 'react-hook-form'

import s from './styles.module.scss'

import { ControlledCommons } from '../../atoms/fields/common.interface'
import Icon from '../../atoms/icons/icon'
import { MobileBackPage } from '../../layout'
import {
  breakpoints,
  useMediaQuery,
  useOnClickOutside,
} from '../../../lib/hooks'
import { Mixpanel } from '../../../utils/Mixpanel'

export interface IControlledModalPickerProps extends ControlledCommons {
  dataGtm: string
  dataMixpanel?: { name: string; data?: any }
  renderContentModal: (
    field: ControllerRenderProps<FieldValues, string>,
  ) => React.ReactElement
  renderInputField: (
    field: ControllerRenderProps<FieldValues, string>,
  ) => React.ReactElement
  mobileTitle?: React.ReactNode
  mobileFooter?: React.ReactElement
  isCentered?: boolean
  isLeftAligned?: boolean
  className?: string
  modalClassName?: string
  containerClassName?: string
  innerModalClassName?: string
  onClose?: () => void
  onOpen?: () => void
  disabled?: boolean
  onClickOutside?: () => void
  isFullScreen?: boolean
  revampPicker?: boolean
}

export type ModalPickerRef = {
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>
}

const ModalPicker = forwardRef<ModalPickerRef, IControlledModalPickerProps>(
  (
    {
      control,
      rules,
      name,
      dataGtm,
      dataMixpanel,
      renderContentModal,
      renderInputField,
      mobileTitle,
      mobileFooter,
      isCentered = false,
      isLeftAligned = false,
      className,
      modalClassName,
      containerClassName,
      innerModalClassName,
      onClose,
      onOpen,
      disabled = false,
      onClickOutside,
      isFullScreen = false,
      revampPicker = false,
    }: IControlledModalPickerProps,
    refParent,
  ) => {
    const [showModal, setShowModal] = useState<boolean>(false)
    const isMobile = useMediaQuery(breakpoints.newMobile)

    const ref = useRef<HTMLDivElement>(null)

    useImperativeHandle(refParent, () => ({ setShowModal }))

    useEffect(() => {
      if (showModal === false) {
        onClose && onClose()
      } else {
        onOpen && onOpen()
      }
    }, [onClose, onOpen, showModal])

    useOnClickOutside(ref, () => {
      if (showModal) {
        onClickOutside && onClickOutside()
        setShowModal(false)
      }
    })

    const onCloseModal = () => {
      setShowModal(false)
    }

    if ((isMobile || isFullScreen) && showModal) {
      return (
        <div
          onClick={() => {
            if (dataMixpanel) {
              Mixpanel.track(dataMixpanel?.name, dataMixpanel?.data)
            }
          }}
        >
          <Controller
            control={control}
            rules={rules}
            render={({ field }) => (
              <MobileBackPage
                backPageProps={{
                  onGoBack: !revampPicker ? onCloseModal : undefined,
                  title: mobileTitle,
                  extra: revampPicker
                    ? {
                        icon: (
                          <Icon
                            name='cross-without-circle'
                            size={16}
                            color='currentColor'
                          />
                        ),
                        onClick: onCloseModal,
                      }
                    : undefined,
                }}
                isModal
                Footer={mobileFooter}
              >
                <div className={s.contentMobileModal}>
                  {renderContentModal(field)}
                </div>
              </MobileBackPage>
            )}
            name={name}
          />
        </div>
      )
    }

    return (
      <Controller
        control={control}
        rules={rules}
        render={({ field }) => {
          return (
            <div className={clsx(s.container, className)} ref={ref}>
              <div
                className={clsx(s.container, containerClassName)}
                data-gtm={dataGtm}
                onClick={(e) => {
                  if (disabled) {
                    return
                  }
                  if (dataMixpanel) {
                    Mixpanel.track(dataMixpanel?.name, dataMixpanel?.data)
                  }
                  e.stopPropagation()
                  setShowModal(!showModal)
                }}
              >
                {renderInputField(field)}
              </div>

              {showModal && (
                <div
                  className={clsx(
                    s.containerModal,
                    modalClassName,
                    { [s.centered]: isCentered },
                    { [s.leftAligned]: isLeftAligned },
                  )}
                  id='modal-picker'
                  data-testid='modal-picker'
                >
                  <div className={clsx(s.modal, innerModalClassName)}>
                    {renderContentModal(field)}
                  </div>
                </div>
              )}
            </div>
          )
        }}
        name={name}
      />
    )
  },
)

ModalPicker.displayName = 'ModalPicker'

export { ModalPicker }
